import { useEffect, useState } from "react";

import {
  observeMembership,
  observeTeamCollection,
  observeUserDirectory,
} from "../data/membershipRepository";
import type {
  ClaimModel,
  MemberModel,
  PlayerModel,
  UserDirectoryModel,
} from "../shared/types/contracts";

interface MembershipState {
  member: MemberModel | null | undefined;
  error: unknown | null;
}

interface UserDirectoryState {
  directory: UserDirectoryModel | null | undefined;
  error: unknown | null;
}

type TeamCollectionModels = {
  claims: ClaimModel;
  players: PlayerModel;
};

interface TeamCollectionState<T> {
  docs: T[] | undefined;
  error: unknown | null;
}

export function useMembership(
  teamId: string | null | undefined,
  uid: string | null | undefined,
): MembershipState {
  const [state, setState] = useState<MembershipState>({
    member: undefined,
    error: null,
  });
  useEffect(() => {
    if (!teamId || !uid) {
      setState({ member: undefined, error: null });
      return;
    }
    setState({ member: undefined, error: null });
    return observeMembership(
      teamId,
      uid,
      (member) => setState({ member, error: null }),
      (error) => setState({ member: undefined, error }),
    );
  }, [teamId, uid]);
  return state;
}

export function useUserDirectory(
  uid: string | null | undefined,
): UserDirectoryState {
  const [state, setState] = useState<UserDirectoryState>({
    directory: undefined,
    error: null,
  });
  useEffect(() => {
    if (!uid) {
      setState({ directory: undefined, error: null });
      return;
    }
    setState({ directory: undefined, error: null });
    return observeUserDirectory(
      uid,
      (directory) => setState({ directory, error: null }),
      (error) => setState({ directory: undefined, error }),
    );
  }, [uid]);
  return state;
}

export function useTeamCollection<Name extends keyof TeamCollectionModels>(
  teamId: string | null | undefined,
  name: Name,
): TeamCollectionState<TeamCollectionModels[Name]> {
  const [state, setState] = useState<
    TeamCollectionState<TeamCollectionModels[Name]>
  >({ docs: undefined, error: null });
  useEffect(() => {
    if (!teamId) {
      setState({ docs: undefined, error: null });
      return;
    }
    setState({ docs: undefined, error: null });
    try {
      return observeTeamCollection(
        teamId,
        name,
        (docs) => setState({ docs, error: null }),
        (error) => setState({ docs: undefined, error }),
      );
    } catch (error) {
      setState({ docs: undefined, error });
      return undefined;
    }
  }, [teamId, name]);
  return state;
}
